/**
 * GoLive Page - Multi-platform livestreaming for Studio V2
 *
 * Standalone livestreaming feature allowing users to stream to
 * YouTube, Twitch, Facebook, LinkedIn, and custom RTMP destinations.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudioStore } from '../App';
import { StreamPreview } from '../components/golive/StreamPreview';
import { PlatformConfig } from '../components/golive/PlatformConfig';
import { LiveDashboard } from '../components/golive/LiveDashboard';
import {
  startLivestream,
  stopLivestream,
  getLivestreamStatus,
  getStreamStats,
  addPlatform,
  removePlatform,
  startRecording,
  stopRecording,
  Platform,
  StreamStats,
  LivestreamSession,
} from '../lib/golive';
import { VideoSDKManager, LivestreamOutput } from '../lib/videosdk';
import '../styles/golive.css';

type StreamState = 'idle' | 'starting' | 'live' | 'stopping';

export function GoLive() {
  // TOP-LEVEL LOG - This runs on every render
  // Cache bust: 2026-02-03 - Using VideoSDK native RTMP streaming (no WHIPConnection)
  console.log('[GoLive] ========== COMPONENT RENDERING ==========', {
    timestamp: new Date().toISOString(),
    version: 'v2.1.0-rtmp-native', // Force cache invalidation
  });

  const navigate = useNavigate();
  const { token, userId, userName } = useStudioStore();

  // Stream state
  const [streamState, setStreamState] = useState<StreamState>('idle');
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [session, setSession] = useState<LivestreamSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Platform configuration
  const [platforms, setPlatforms] = useState<Platform[]>([]);

  // Recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordOnStart, setRecordOnStart] = useState(false);

  // Stats
  const [stats, setStats] = useState<StreamStats | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);

  // VideoSDK manager ref for native RTMP streaming
  const videoSDKManager = useRef<VideoSDKManager | null>(null);
  const statsInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  // MediaStream ref for cleanup (GoLive owns the stream after StreamPreview passes it)
  const mediaStreamRef = useRef<MediaStream | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!token) {
      const returnUrl = encodeURIComponent('https://www.mypodops.com/studio-v2/live');
      window.location.href = `https://www.mypodops.com/login?return=${returnUrl}`;
    }
  }, [token]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('[GoLive] Cleanup on unmount - stopping resources');
      if (statsInterval.current) {
        clearInterval(statsInterval.current);
      }
      if (videoSDKManager.current) {
        videoSDKManager.current.stopLivestream();
      }
      // Stop media stream tracks (GoLive owns the stream after StreamPreview passes it)
      if (mediaStreamRef.current) {
        console.log('[GoLive] Stopping media stream tracks:', mediaStreamRef.current.id);
        mediaStreamRef.current.getTracks().forEach((track) => {
          console.log('[GoLive] Stopping track:', track.kind, track.label);
          track.stop();
        });
        mediaStreamRef.current = null;
      }
    };
  }, []);

  // Poll for stats when live
  useEffect(() => {
    if (streamState === 'live' && session) {
      statsInterval.current = setInterval(async () => {
        try {
          const newStats = await getStreamStats(session.id);
          setStats(newStats);

          // Update platform statuses
          const status = await getLivestreamStatus(session.id);
          if (status.platforms) {
            setPlatforms(status.platforms);
          }
        } catch (err) {
          console.error('Failed to fetch stats:', err);
        }
      }, 5000); // Poll every 5 seconds

      return () => {
        if (statsInterval.current) {
          clearInterval(statsInterval.current);
        }
      };
    }
  }, [streamState, session]);

  // Handle stream ready from preview
  const handleStreamReady = useCallback((stream: MediaStream) => {
    console.log('[GoLive] handleStreamReady called with stream:', {
      active: stream?.active,
      id: stream?.id,
      videoTracks: stream?.getVideoTracks()?.length,
      audioTracks: stream?.getAudioTracks()?.length,
    });
    // Store in ref for cleanup (GoLive now owns this stream)
    mediaStreamRef.current = stream;
    setMediaStream(stream);
    setError(null);
  }, []);

  // Debug: Track mediaStream state changes
  useEffect(() => {
    console.log('[GoLive] mediaStream state changed:', {
      hasStream: !!mediaStream,
      active: mediaStream?.active,
      id: mediaStream?.id,
    });
  }, [mediaStream]);

  // Handle stream error from preview
  const handleStreamError = useCallback((err: Error) => {
    console.log('[GoLive] handleStreamError called:', err.message);
    setError(err.message);
    mediaStreamRef.current = null;
    setMediaStream(null);
  }, []);

  // Handle platform changes
  const handlePlatformsChange = useCallback((newPlatforms: Platform[]) => {
    setPlatforms(newPlatforms);
  }, []);

  // Start livestream
  const handleGoLive = async () => {
    console.log('[GoLive] handleGoLive clicked, checking mediaStream:', {
      hasStream: !!mediaStream,
      active: mediaStream?.active,
      id: mediaStream?.id,
      userId,
    });

    if (!mediaStream || !userId) {
      console.error('[GoLive] Go Live failed - no media stream or userId:', { mediaStream: !!mediaStream, userId });
      setError('No media stream available');
      return;
    }

    const enabledPlatforms = platforms.filter(p => p.enabled);
    if (enabledPlatforms.length === 0) {
      setError('Please enable at least one platform');
      return;
    }

    setStreamState('starting');
    setError(null);

    try {
      // Start the livestream session on backend
      const newSession = await startLivestream({
        platforms: enabledPlatforms.map(p => ({
          platform: p.name,
          rtmpUrl: p.rtmpUrl,
          streamKey: p.streamKey,
        })),
        enableRecording: recordOnStart,
      });

      setSession(newSession);

      // Convert platforms to VideoSDK LivestreamOutput format
      const livestreamOutputs: LivestreamOutput[] = enabledPlatforms.map(p => ({
        url: p.rtmpUrl,
        streamKey: p.streamKey,
      }));

      // Initialize VideoSDK manager and start native RTMP streaming
      // This bypasses the SRS/WHIP connection which has mixed content issues
      videoSDKManager.current = new VideoSDKManager();
      await videoSDKManager.current.startLivestream(livestreamOutputs);
      console.log('[GoLive] Started VideoSDK native RTMP streaming to', livestreamOutputs.length, 'platforms');

      // Set state
      setStreamState('live');
      setStartTime(new Date());
      setIsRecording(recordOnStart);

      // Update platforms with initial status
      setPlatforms(newSession.platforms || enabledPlatforms.map(p => ({
        ...p,
        status: 'connecting' as const,
      })));

    } catch (err) {
      console.error('Failed to start livestream:', err);
      setError(err instanceof Error ? err.message : 'Failed to start livestream');
      setStreamState('idle');

      // Cleanup on failure
      if (videoSDKManager.current) {
        videoSDKManager.current.stopLivestream();
        videoSDKManager.current = null;
      }
    }
  };

  // Stop livestream
  const handleStopStream = async () => {
    if (!session) return;

    setStreamState('stopping');

    try {
      // Stop VideoSDK native RTMP streaming
      if (videoSDKManager.current) {
        await videoSDKManager.current.stopLivestream();
        videoSDKManager.current = null;
        console.log('[GoLive] Stopped VideoSDK native RTMP streaming');
      }

      // Stop on backend
      await stopLivestream(session.id);

      // Reset state
      setSession(null);
      setStreamState('idle');
      setStartTime(null);
      setStats(null);
      setIsRecording(false);

      // Reset platform statuses
      setPlatforms(platforms.map(p => ({
        ...p,
        status: 'disconnected' as const,
        viewerCount: 0,
      })));

    } catch (err) {
      console.error('Failed to stop livestream:', err);
      setError(err instanceof Error ? err.message : 'Failed to stop livestream');
      // Force reset state anyway
      setStreamState('idle');
    }
  };

  // Toggle recording mid-stream
  const handleToggleRecording = async () => {
    if (!session) return;

    try {
      if (isRecording) {
        await stopRecording(session.id);
        setIsRecording(false);
      } else {
        await startRecording(session.id);
        setIsRecording(true);
      }
    } catch (err) {
      console.error('Failed to toggle recording:', err);
      setError(err instanceof Error ? err.message : 'Failed to toggle recording');
    }
  };

  // Add platform mid-stream
  const handleAddPlatformLive = async (platform: Platform) => {
    if (!session) {
      // Not live yet, just add to local state
      setPlatforms([...platforms, { ...platform, enabled: true }]);
      return;
    }

    try {
      await addPlatform(session.id, {
        platform: platform.name,
        rtmpUrl: platform.rtmpUrl,
        streamKey: platform.streamKey,
      });
      setPlatforms([...platforms, { ...platform, enabled: true, status: 'connecting' }]);
    } catch (err) {
      console.error('Failed to add platform:', err);
      setError(err instanceof Error ? err.message : 'Failed to add platform');
    }
  };

  // Remove platform mid-stream
  const handleRemovePlatformLive = async (platformId: string) => {
    if (!session) {
      // Not live yet, just remove from local state
      setPlatforms(platforms.filter(p => p.id !== platformId));
      return;
    }

    try {
      await removePlatform(session.id, platformId);
      setPlatforms(platforms.filter(p => p.id !== platformId));
    } catch (err) {
      console.error('Failed to remove platform:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove platform');
    }
  };

  const isLive = streamState === 'live';
  const canGoLive = mediaStream && platforms.some(p => p.enabled) && streamState === 'idle';

  // LOG: Show critical state before render
  console.log('[GoLive] About to render, state:', {
    isLive,
    canGoLive,
    hasMediaStream: !!mediaStream,
    streamState,
    platformsCount: platforms.length,
    enabledPlatformsCount: platforms.filter(p => p.enabled).length,
  });

  return (
    <div className="golive-page">
      {/* Header */}
      <header className="golive-header">
        <div className="golive-header-left">
          <button
            className="golive-back-btn"
            onClick={() => navigate('/')}
            disabled={isLive}
          >
            ← Back to Studio
          </button>
          <h1>Go Live</h1>
        </div>
        <div className="golive-header-right">
          {streamState === 'idle' && (
            <button
              className="golive-btn golive-btn-primary"
              onClick={handleGoLive}
              disabled={!canGoLive}
            >
              Go Live
            </button>
          )}
          {streamState === 'starting' && (
            <button className="golive-btn golive-btn-disabled" disabled>
              Starting...
            </button>
          )}
          {streamState === 'stopping' && (
            <button className="golive-btn golive-btn-disabled" disabled>
              Stopping...
            </button>
          )}
        </div>
      </header>

      {/* Error banner */}
      {error && (
        <div className="golive-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Main content */}
      <main className="golive-main">
        {!isLive ? (
          // Pre-live setup view
          <div className="golive-setup">
            {/* Preview section */}
            <section className="golive-preview-section">
              <StreamPreview
                onStreamReady={handleStreamReady}
                onStreamError={handleStreamError}
                isLive={isLive}
              />
            </section>

            {/* Platform configuration */}
            <section className="golive-config-section">
              <PlatformConfig
                platforms={platforms}
                onPlatformsChange={handlePlatformsChange}
                isLive={isLive}
              />

              {/* Recording option */}
              <div className="golive-record-option">
                <label className="golive-checkbox">
                  <input
                    type="checkbox"
                    checked={recordOnStart}
                    onChange={(e) => setRecordOnStart(e.target.checked)}
                  />
                  <span className="golive-checkbox-label">
                    Also record this stream
                  </span>
                </label>
                <p className="golive-checkbox-hint">
                  Recording will be saved to your Studio recordings
                </p>
              </div>
            </section>
          </div>
        ) : (
          // Live dashboard view
          <div className="golive-live">
            {/* Small preview */}
            <section className="golive-live-preview">
              <StreamPreview
                onStreamReady={handleStreamReady}
                onStreamError={handleStreamError}
                isLive={isLive}
              />
            </section>

            {/* Live dashboard */}
            <section className="golive-live-dashboard">
              <LiveDashboard
                stats={stats}
                platforms={platforms}
                startTime={startTime}
                isRecording={isRecording}
                onStopStream={handleStopStream}
                onToggleRecording={handleToggleRecording}
              />
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

export default GoLive;
